/**
 * Science.tsx
 * =============================================================================
 * Página del Manifiesto Científico de Neuro-Audio Lab.
 * Explica la base científica detrás de cada protocolo de audio.
 *
 * Diseño: Dark Mode Academic - Fondo oscuro, tipografía limpia, iconografía científica.
 *
 * @author Neuro-Audio Lab
 * @version 1.0.0
 */

import { ArrowLeft, Brain, Waves, Activity, FlaskConical, CheckCircle2, BellRing, Infinity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { APP_NAME, APP_VERSION, APP_STUDIO, APP_COUNTRY } from '../config/constants';

// =============================================================================
// COMPONENT
// =============================================================================

export function Science() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-300">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-6 left-6 z-50 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
        aria-label="Volver"
      >
        <ArrowLeft className="w-5 h-5 text-neutral-300" />
      </button>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-16">

        {/* ================================================================ */}
        {/* HERO SECTION */}
        {/* ================================================================ */}
        <section className="text-center space-y-6 pt-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20">
            <FlaskConical className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-blue-300 font-medium tracking-wider uppercase">
              Manifiesto Científico
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-light tracking-tight text-neutral-200">
            La Ingeniería de tu Estado Mental
          </h1>

          <p className="text-xl text-neutral-400 font-light max-w-2xl mx-auto">
            No hacemos música para relajarte. Diseñamos audio para{' '}
            <span className="text-neutral-100 font-medium">regularte</span>.
          </p>

          <div className="pt-4 max-w-3xl mx-auto text-left">
            <p className="text-base leading-relaxed text-neutral-400">
              En Neuro-Audio Lab, no componemos canciones; construimos{' '}
              <span className="text-neutral-100">Arquitectura Cognitiva</span>. Utilizamos
              principios de psicoacústica y neurobiología para crear entornos sonoros
              que "hackean" (de forma ética y biológica) tu sistema nervioso autónomo:
              del caos al enfoque, de la alerta al sueño, de la ansiedad a la calma.
            </p>
          </div>
        </section>

        {/* ================================================================ */}
        {/* LOS 3 MECANISMOS */}
        {/* ================================================================ */}
        <section className="space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-light text-neutral-200 mb-3">
              Los 4 Mecanismos
            </h2>
            <p className="text-sm text-neutral-400">
              Nos basamos en cuatro pilares validados por la literatura científica{' '}
              <sup className="text-blue-400/60 text-xs">[1-4]</sup>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Card A: Arrastre Neuronal */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all">
              <div className="mb-4 p-3 rounded-xl bg-blue-500/10 w-fit">
                <Brain className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-neutral-100 mb-3">
                Arrastre Neuronal
              </h3>
              <p className="text-sm leading-relaxed text-neutral-400">
                Tu cerebro sincroniza su frecuencia eléctrica con ritmos acústicos externos. Utilizamos modulación de amplitud y pulsos isocrónicos incrustados en las texturas sonoras para invitar a tu sistema a cambiar de marcha: frecuencias Beta (Enfoque), Alpha (Calma) o Delta (Sueño) <sup className="text-blue-400/60 text-xs">[5]</sup>.
              </p>
            </div>

            {/* Card B: Reducción de la Saliencia */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all">
              <div className="mb-4 p-3 rounded-xl bg-purple-500/10 w-fit">
                <Waves className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-medium text-neutral-100 mb-3">
                Reducción de la Saliencia
              </h3>
              <p className="text-sm leading-relaxed text-neutral-400">
                La música tradicional compite por tu atención con cambios bruscos. Nuestra tecnología utiliza camas sonoras de banda ancha (como ruido marrón esculpido o el flujo orgánico de un río) para enmascarar picos acústicos transitorios. Esto "suaviza" tu entorno, reduciendo las alertas inconscientes de la amígdala y liberando recursos para tu enfoque o descanso <sup className="text-blue-400/60 text-xs">[6-9]</sup>.
              </p>
            </div>

            {/* Card C: Estimulación Bilateral */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all">
              <div className="mb-4 p-3 rounded-xl bg-green-500/10 w-fit">
                <Activity className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-lg font-medium text-neutral-100 mb-3">
                Estimulación Bilateral
              </h3>
              <p className="text-sm leading-relaxed text-neutral-400">
                Para la ansiedad, usamos paneo lento (el sonido viaja suavemente de un
                oído a otro). Esto simula mecanismos clínicos (como EMDR) para ayudar a
                reducir la actividad de la amígdala cerebral y procesar emociones
                bloqueadas{' '}
                <sup className="text-blue-400/60 text-xs">[10]</sup>.
              </p>
            </div>

            {/* Card D: Condicionamiento Acústico */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all">
              <div className="mb-4 p-3 rounded-xl bg-amber-500/10 w-fit">
                <BellRing className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-medium text-neutral-100 mb-3">
                Condicionamiento Acústico
              </h3>
              <p className="text-sm leading-relaxed text-neutral-400">
                Empleamos marcadores sonoros (triggers) al inicio de nuestras sesiones de regulación para capitalizar el condicionamiento clásico pavloviano. Con la repetición sistemática, tu cerebro asocia esta señal acústica específica con el inicio de un estado de descanso profundo o desactivación de la ansiedad, reduciendo el tiempo biológico de transición <sup className="text-blue-400/60 text-xs">[11]</sup>.
              </p>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* EL ESTÁNDAR POLÍGONO */}
        {/* ================================================================ */}
        <section className="space-y-6">
          <div className="text-center">
            <h2 className="text-3xl font-light text-neutral-200 mb-3">
              El Estándar Polígono
            </h2>
            <p className="text-lg text-neutral-400 italic">
              Nuestros sonidos son 100% hechos por humanos, ninguna IA involucrada.
            </p>
          </div>

          <div className="space-y-4 max-w-3xl mx-auto">
            {/* Punto 1 */}
            <div className="flex items-start gap-4 p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-base font-medium text-neutral-100 mb-2">
                  Fidelidad Biológica
                </h3>
                <p className="text-sm leading-relaxed text-neutral-400">
                  No utilizamos ruido estático digital. Nuestras texturas de enmascaramiento están esculpidas a partir de grabaciones de campo reales. Aplicamos filtros psicoacústicos quirúrgicos para atenuar resonancias punzantes y retumbos graves, garantizando un espectro de frecuencias envolvente que no genera fatiga auditiva a largo plazo <sup className="text-blue-400/60 text-xs">[11]</sup>.
                </p>
              </div>
            </div>

            {/* Punto 2 */}
            <div className="flex items-start gap-4 p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-base font-medium text-neutral-100 mb-2">
                  Inmersión Documental
                </h3>
                <p className="text-sm leading-relaxed text-neutral-400">
                  Nuestros paisajes sonoros son grabaciones documentales de alta
                  resolución de la biodiversidad de los Andes y la Amazonía (Ecuador).
                  Es biofilia acústica real, no librerías de stock{' '}
                  <sup className="text-blue-400/60 text-xs">[12-13]</sup>.
                </p>
              </div>
            </div>

            {/* Punto 3 */}
            <div className="flex items-start gap-4 p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <Infinity className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-base font-medium text-neutral-100 mb-2">
                  Inmersión Matemática Continua
                </h3>
                <p className="text-sm leading-relaxed text-neutral-400">
                  Cualquier micro-corte en un bucle de audio genera una &quot;respuesta de orientación&quot; inconsciente que rompe el estado de flujo. Utilizamos compresión Opus y arquitectura Web Audio API nativa para garantizar <i>gapless playback</i>: ecosistemas sonoros infinitos y sample-accurate, blindando tu sistema nervioso de distracciones digitales <sup className="text-blue-400/60 text-xs">[14]</sup>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* PROTOCOLOS */}
        {/* ================================================================ */}
        <section className="space-y-6">
          <div className="text-center">
            <h2 className="text-3xl font-light text-neutral-200 mb-3">
              Protocolos
            </h2>
            <p className="text-sm text-neutral-400">
              Cada módulo está diseñado para una transición de estado específica.
            </p>
          </div>

          <div className="space-y-3 max-w-3xl mx-auto">
            {/* FOCUS */}
            <div className="p-5 rounded-xl bg-blue-500/5 border border-blue-500/10">
              <h3 className="text-base font-semibold text-blue-300 mb-2 uppercase tracking-wider">
                Focus
              </h3>
              <p className="text-sm leading-relaxed text-neutral-400">
                Ruido marrón + Beta. Reduce la fatiga por cambio de contexto (context
                switching){' '}
                <sup className="text-blue-400/60 text-xs">[14]</sup>. Ideal para
                programación y trabajo profundo.
              </p>
            </div>

            {/* SLEEP */}
            <div className="p-5 rounded-xl bg-purple-500/5 border border-purple-500/10">
              <h3 className="text-base font-semibold text-purple-300 mb-2 uppercase tracking-wider">
                Sleep
              </h3>
              <p className="text-sm leading-relaxed text-neutral-400">
                Ondas Delta + Paisajes de Ecuador. Usa un fade-out generativo que
                acompaña tu ciclo natural para entrar en fases NREM más rápido{' '}
                <sup className="text-blue-400/60 text-xs">[15]</sup>.
              </p>
            </div>

            {/* RESET */}
            <div className="p-5 rounded-xl bg-green-500/5 border border-green-500/10">
              <h3 className="text-base font-semibold text-green-300 mb-2 uppercase tracking-wider">
                Reset
              </h3>
              <p className="text-sm leading-relaxed text-neutral-400">
                Estimulación Bilateral + Suspiro Fisiológico. Combina un paneo lento para modular la amígdala con una guía respiratoria de <i>Cyclic Sighing</i> (inhalación doble y exhalación prolongada). Basado en ensayos clínicos de la Universidad de Stanford (Spiegel & Huberman, 2023), esta pauta desactiva mecánicamente la respuesta simpática en tiempo real <sup className="text-blue-400/60 text-xs">[18]</sup>.
              </p>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* FOOTER (VALIDACIÓN) */}
        {/* ================================================================ */}
        <section className="pt-8 border-t border-white/5">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h3 className="text-xl font-medium text-neutral-100">
              Ciencia Abierta
            </h3>
            <p className="text-sm leading-relaxed text-neutral-400">
              Estamos desplegando un estudio piloto para medir la eficacia de nuestros
              protocolos (HRV, STAI). No te pedimos fe ciega, te ofrecemos herramientas
              medibles{' '}
              <sup className="text-blue-400/60 text-xs">[17]</sup>.
            </p>
          </div>
        </section>

        {/* Bottom Spacer */}
        <div className="h-12" />
      </div>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-white/5">
        <p className="text-xs text-neutral-400">
          {`${APP_NAME} ${APP_VERSION} · ${APP_STUDIO} · ${APP_COUNTRY} 🇪🇨`}
        </p>
      </footer>
    </div>
  );
}

export default Science;
